// src/features/subscription/BundleFormDialog.tsx
// Create / edit a product bundle — one form-kit dialog shared by both flows
// (docs/ux/14-forms.md), opened from BundleListPage's "+ New" and from
// BundleDetailPage's "Edit" button. Money fields are entered/displayed in
// major units and converted to minor units on submit, same convention as
// GrantCreditModal. Bundle line items (which products/plans are included)
// are managed on the detail page — see the deferral note in the forms-wave
// report; this dialog covers the bundle's own catalog fields only.

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useSubscriptionBundle, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import { errMessage, toMajorUnitsText, toMinorUnits } from './format';
import type { ProductBundle } from '@core/types/subscription/ProductBundle';
import type { ProductBundleBillingMode } from '@core/types/subscription/ProductBundleBillingMode';
import type { ProductBundleStatus } from '@core/types/subscription/ProductBundleStatus';
import type { DiscountType } from '@core/types/subscription/DiscountType';
import type { Currency } from '@core/types/subscription/Currency';
import type { BillingCycle } from '@core/types/subscription/BillingCycle';

const BILLING_MODES: ProductBundleBillingMode[] = ['PRODUCT_BUNDLE_BILLING_MODE_SINGLE', 'PRODUCT_BUNDLE_BILLING_MODE_LINKED'];
const STATUSES: ProductBundleStatus[] = ['PRODUCT_BUNDLE_STATUS_DRAFT', 'PRODUCT_BUNDLE_STATUS_ACTIVE', 'PRODUCT_BUNDLE_STATUS_SUSPENDED', 'PRODUCT_BUNDLE_STATUS_ARCHIVED'];
const DISCOUNT_TYPES: DiscountType[] = ['DISCOUNT_TYPE_PERCENTAGE', 'DISCOUNT_TYPE_FIXED_AMOUNT', 'DISCOUNT_TYPE_FREE_PERIOD'];
const CURRENCIES: Currency[] = ['CURRENCY_USD', 'CURRENCY_EUR', 'CURRENCY_RUB', 'CURRENCY_GBP', 'CURRENCY_JPY', 'CURRENCY_CNY', 'CURRENCY_KZT', 'CURRENCY_BYN', 'CURRENCY_AED', 'CURRENCY_TRY'];
const BILLING_CYCLES: BillingCycle[] = ['BILLING_CYCLE_MONTHLY', 'BILLING_CYCLE_QUARTERLY', 'BILLING_CYCLE_SEMI_ANNUALLY', 'BILLING_CYCLE_ANNUALLY', 'BILLING_CYCLE_DAILY', 'BILLING_CYCLE_WEEKLY', 'BILLING_CYCLE_BIWEEKLY', 'BILLING_CYCLE_CUSTOM'];

function digits(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

function decimalText(v: string): string | null {
  return /^\d*\.?\d{0,2}$/.test(v) ? v : null;
}

function slugify(v: string): string {
  return v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface FormState {
  name: string;
  description: string;
  slug: string;
  tagline: string;
  billing_mode: ProductBundleBillingMode;
  bundle_price: string; // major units text
  currency: Currency;
  billing_cycle: BillingCycle;
  discount_type: DiscountType;
  discount_value: string; // major units text when FIXED_AMOUNT, plain number otherwise
  trial_period_days: string;
  is_public: boolean;
  max_subscriptions: string;
  status: ProductBundleStatus;
}

function snapshotOf(bundle: ProductBundle | null): FormState {
  const discountType = bundle?.discount_type ?? 'DISCOUNT_TYPE_PERCENTAGE';
  return {
    name: bundle?.name ?? '',
    description: bundle?.description ?? '',
    slug: bundle?.slug ?? '',
    tagline: bundle?.tagline ?? '',
    billing_mode: bundle?.billing_mode ?? 'PRODUCT_BUNDLE_BILLING_MODE_SINGLE',
    bundle_price: toMajorUnitsText(bundle?.bundle_price),
    currency: bundle?.currency ?? 'CURRENCY_USD',
    billing_cycle: bundle?.billing_cycle ?? 'BILLING_CYCLE_MONTHLY',
    discount_type: discountType,
    discount_value: discountType === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? toMajorUnitsText(bundle?.discount_value) : (bundle?.discount_value ? String(bundle.discount_value) : ''),
    trial_period_days: bundle?.trial_period_days ? String(bundle.trial_period_days) : '',
    is_public: bundle?.is_public ?? true,
    max_subscriptions: bundle?.max_subscriptions ? String(bundle.max_subscriptions) : '',
    status: bundle?.status ?? 'PRODUCT_BUNDLE_STATUS_DRAFT',
  };
}

export interface BundleFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Bundle being edited; null → create. */
  bundle: ProductBundle | null;
  onSaved: () => void;
}

export const BundleFormDialog = observer(({ open, onClose, bundle, onSaved }: BundleFormDialogProps) => {
  const { t } = useI18n();
  const store = useSubscriptionBundle();
  const isEdit = !!bundle;

  const [form, setForm] = useState<FormState>(() => snapshotOf(bundle));
  const [initial, setInitial] = useState<FormState>(() => snapshotOf(bundle));
  const [slugTouched, setSlugTouched] = useState(false);
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const snap = snapshotOf(bundle);
    setForm(snap);
    setInitial(snap);
    setSlugTouched(isEdit);
    setMoreFields(false);
    setError(null);
  }, [open, bundle]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const canSubmit = form.name.trim().length > 0 && form.slug.trim().length > 0;

  const billingModeOptions = BILLING_MODES.map((v) => ({ value: v, label: t(`subscription.bundle.billingMode.${v.replace('PRODUCT_BUNDLE_BILLING_MODE_', '').toLowerCase()}`) }));
  const statusOptions = STATUSES.map((v) => ({ value: v, label: t(`subscription.bundle.status.${v.replace('PRODUCT_BUNDLE_STATUS_', '').toLowerCase()}`) }));
  const discountTypeOptions = DISCOUNT_TYPES.map((v) => ({ value: v, label: t(`subscription.discount.type.${v.replace('DISCOUNT_TYPE_', '').toLowerCase()}`) }));
  const currencyOptions = CURRENCIES.map((v) => ({ value: v, label: t(`subscription.currency.${v.replace('CURRENCY_', '').toLowerCase()}`) }));
  const billingCycleOptions = BILLING_CYCLES.map((v) => ({ value: v, label: t(`subscription.billingCycle.${v.replace('BILLING_CYCLE_', '').toLowerCase()}`) }));

  const resolvedDiscountValue = () =>
    form.discount_type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? toMinorUnits(form.discount_value) : Number(form.discount_value || 0);

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && bundle) {
        await store.UpdateProductBundle(bundle.entity?.id ?? '', {
          bundle: {
            ...bundle,
            name: form.name.trim(),
            description: form.description,
            slug: form.slug.trim(),
            tagline: form.tagline,
            status: form.status,
            billing_mode: form.billing_mode,
            bundle_price: toMinorUnits(form.bundle_price),
            currency: form.currency,
            billing_cycle: form.billing_cycle,
            discount_type: form.discount_type,
            discount_value: resolvedDiscountValue(),
            trial_period_days: form.trial_period_days ? Number(form.trial_period_days) : 0,
            is_public: form.is_public,
            max_subscriptions: form.max_subscriptions ? Number(form.max_subscriptions) : 0,
          },
          field_mask: ['name', 'description', 'slug', 'tagline', 'status', 'billing_mode', 'bundle_price', 'currency', 'billing_cycle', 'discount_type', 'discount_value', 'trial_period_days', 'is_public', 'max_subscriptions'],
        });
      } else {
        await store.CreateProductBundle({
          name: form.name.trim(),
          description: form.description,
          slug: form.slug.trim(),
          tagline: form.tagline,
          billing_mode: form.billing_mode,
          bundle_price: toMinorUnits(form.bundle_price),
          currency: form.currency,
          billing_cycle: form.billing_cycle,
          discount_type: form.discount_type,
          discount_value: resolvedDiscountValue(),
          trial_period_days: form.trial_period_days ? Number(form.trial_period_days) : 0,
          is_public: form.is_public,
          max_subscriptions: form.max_subscriptions ? Number(form.max_subscriptions) : 0,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('subscription.bundle.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('subscription.bundle.create')}
      canSubmit={canSubmit}
      isSubmitting={saving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('subscription.bundle.edit') : t('subscription.bundle.create')}
      footerLeft={
        <button type="button" onClick={() => setMoreFields((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground">
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <Alert variant="error">{error}</Alert>}
        <TitleInput
          value={form.name}
          onChange={(v) => { set('name', v); if (!slugTouched && !isEdit) set('slug', slugify(v)); }}
          placeholder={`${t('subscription.bundle.field.name')}…`}
          initialFocus
          aria-label={t('subscription.bundle.field.name')}
        />
        <AutoGrowTextarea
          value={form.description}
          onChange={(v) => set('description', v)}
          placeholder={`${t('subscription.bundle.field.description')}…`}
          minRows={1}
        />
      </div>

      <div className="space-y-1 px-3.5 pb-2">
        <FormRow label={t('subscription.bundle.field.slug')} labelWidth="sm">
          <input
            value={form.slug}
            onChange={(e) => { setSlugTouched(true); set('slug', slugify(e.target.value)); }}
            disabled={isEdit}
            aria-label={t('subscription.bundle.field.slug')}
            className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </FormRow>
        <FormRow label={t('subscription.bundle.field.tagline')} labelWidth="sm">
          <input
            value={form.tagline}
            onChange={(e) => set('tagline', e.target.value)}
            placeholder={`${t('subscription.bundle.field.tagline')}…`}
            aria-label={t('subscription.bundle.field.tagline')}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <SegmentedControl size="sm" value={form.billing_mode} onChange={(v) => set('billing_mode', v as ProductBundleBillingMode)} options={billingModeOptions} aria-label={t('subscription.bundle.field.billingMode')} />
        <div className="w-28">
          <Select value={form.currency} onChange={(e) => set('currency', e.target.value as Currency)} options={currencyOptions} aria-label={t('subscription.bundle.field.currency')} fullWidth />
        </div>
        <div className="w-36">
          <Select value={form.billing_cycle} onChange={(e) => set('billing_cycle', e.target.value as BillingCycle)} options={billingCycleOptions} aria-label={t('subscription.bundle.field.billingCycle')} fullWidth />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={form.is_public} onChange={(e) => set('is_public', e.target.checked)} className="h-4 w-4 rounded border-input" />
          {t('subscription.bundle.field.isPublic')}
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('subscription.bundle.field.price')}</span>
          <input
            type="text"
            inputMode="decimal"
            value={form.bundle_price}
            onChange={(e) => { const v = decimalText(e.target.value); if (v !== null) set('bundle_price', v); }}
            placeholder="0.00"
            aria-label={t('subscription.bundle.field.price')}
            className="w-20 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
          <span className="text-xs text-muted-foreground">{form.currency.replace('CURRENCY_', '')}</span>
        </span>

        <SegmentedControl
          size="sm"
          value={form.discount_type}
          onChange={(v) => {
            const nextType = v as DiscountType;
            // Re-interpret the raw number when switching between money and plain-number semantics.
            set('discount_type', nextType);
          }}
          options={discountTypeOptions}
          aria-label={t('subscription.discount.field.type')}
        />
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <input
            type="text"
            inputMode="decimal"
            value={form.discount_value}
            onChange={(e) => { const v = decimalText(e.target.value); if (v !== null) set('discount_value', v); }}
            placeholder="0"
            aria-label={t('subscription.bundle.field.discountValue')}
            className="w-16 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
          <span className="text-xs text-muted-foreground">
            {form.discount_type === 'DISCOUNT_TYPE_PERCENTAGE' ? '%' : form.discount_type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? form.currency.replace('CURRENCY_', '') : t('subscription.discount.field.months')}
          </span>
        </span>

        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('subscription.bundle.field.trialDays')}</span>
          <input
            type="text"
            inputMode="numeric"
            value={form.trial_period_days}
            onChange={(e) => set('trial_period_days', digits(e.target.value))}
            placeholder="0"
            aria-label={t('subscription.bundle.field.trialDays')}
            className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
        </span>
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <FormRow label={t('subscription.bundle.field.maxSubscriptions')} labelWidth="sm" hint={t('subscription.bundle.field.maxSubscriptionsHint')}>
            <input
              type="text"
              inputMode="numeric"
              value={form.max_subscriptions}
              onChange={(e) => set('max_subscriptions', digits(e.target.value))}
              placeholder={t('subscription.bundle.field.unlimited')}
              aria-label={t('subscription.bundle.field.maxSubscriptions')}
              className="w-24 rounded-md border border-input bg-background px-2.5 py-1.5 text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          {isEdit && (
            <FormRow label={t('subscription.bundle.field.status')} labelWidth="sm">
              <SegmentedControl size="sm" value={form.status} onChange={(v) => set('status', v as ProductBundleStatus)} options={statusOptions} aria-label={t('subscription.bundle.field.status')} />
            </FormRow>
          )}
        </div>
      )}
    </FormDialog>
  );
});

export default BundleFormDialog;
